import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Chip,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { getScoreColor } from '../../theme';
import { colors, fontSize } from '../../theme';
import IchimokuCloudChart from './IchimokuCloudChart';
import { Card, tdSx, thSx, Tile } from './primitives';
import {
  fmtNumber,
  fmtPct,
  fmtPrice,
  getConfidenceLabel,
  getLocalizedText,
  getSignalTypeLabel,
  getStateLabel,
  stateColor,
} from './shared';

function MetricRow({ label, value, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.35 }}>
      <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[500], fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: fontSize.sm, color: color || colors.gray[800], fontWeight: 700, fontFamily: 'monospace' }}>
        {value}
      </Typography>
    </Box>
  );
}

MetricRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  color: PropTypes.string,
};

MetricRow.defaultProps = {
  color: undefined,
};

function DetailDrawer({ open, ticker, historyLoading, detail, onClose, t, language }) {
  const current = detail?.current || null;
  const history = detail?.history || [];
  const chart = detail?.chart || null;
  const scoreBreakdown = current?.score_breakdown || [];
  const reasons = current?.reasons_i18n || [];
  const warnings = current?.warnings_i18n || [];

  let drawerBody = null;
  if (current) {
    drawerBody = (
      <>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
          <Tile
            label={t('ichimokuTransition.drawer.score')}
            value={fmtNumber(current.score, 1)}
            color={getScoreColor(current.score_level)}
          />
          <Tile
            label={t('ichimokuTransition.drawer.setupScore')}
            value={fmtNumber(current.setup_score, 1)}
            color={colors.info.main}
          />
          <Tile
            label={t('ichimokuTransition.drawer.riskPct')}
            value={fmtPct(current.signal?.risk_pct)}
            color={colors.error.main}
          />
          <Tile
            label={t('ichimokuTransition.drawer.rewardPct')}
            value={fmtPct(current.signal?.reward_pct)}
            color={colors.success.main}
          />
        </Box>

        <IchimokuCloudChart chart={chart} signal={current.signal} t={t} />

        <Card>
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.gray[100]}` }}>
            <Typography sx={{ fontSize: fontSize.base, fontWeight: 800, color: colors.gray[800] }}>
              {t('ichimokuTransition.drawer.setupBox')}
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
              <Chip
                label={`${t('ichimokuTransition.table.confidence')}: ${getLocalizedText(current.confidence_label, language, getConfidenceLabel(t, current.confidence))}`}
                size="small"
                sx={{ fontWeight: 700, height: 24 }}
              />
              <Chip
                label={`${t('ichimokuTransition.table.signalType')}: ${getLocalizedText(current.signal_type_label, language, getSignalTypeLabel(t, current.signal_type))}`}
                size="small"
                sx={{ fontWeight: 700, height: 24 }}
              />
              <Chip
                label={`${t('ichimokuTransition.drawer.rr')}: ${fmtNumber(current.signal?.rr_ratio, 2)}`}
                size="small"
                sx={{ fontWeight: 700, height: 24, bgcolor: alphaBg(current.signal?.rr_ratio), color: colors.info.main }}
              />
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.5 }}>
              <MetricRow label={t('ichimokuTransition.drawer.entry')} value={fmtPrice(current.signal?.entry)} />
              <MetricRow label={t('ichimokuTransition.drawer.stop')} value={fmtPrice(current.signal?.stop)} color={colors.error.main} />
              <MetricRow label={t('ichimokuTransition.drawer.target1')} value={fmtPrice(current.signal?.target_1)} color={colors.success.main} />
              <MetricRow label={t('ichimokuTransition.drawer.target2')} value={fmtPrice(current.signal?.target_2)} color={colors.success.main} />
            </Box>
          </Box>
        </Card>

        <Card>
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.gray[100]}` }}>
            <Typography sx={{ fontSize: fontSize.base, fontWeight: 800, color: colors.gray[800] }}>
              {t('ichimokuTransition.drawer.tradeLogic')}
            </Typography>
          </Box>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[700], lineHeight: 1.5 }}>
              <Box component="span" sx={{ fontWeight: 700, color: colors.gray[800] }}>
                {t('ichimokuTransition.drawer.trigger')}:
              </Box>{' '}
              {getLocalizedText(current.signal?.trigger_i18n, language, current.signal?.trigger || '—')}
            </Typography>
            <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[700], lineHeight: 1.5 }}>
              <Box component="span" sx={{ fontWeight: 700, color: colors.gray[800] }}>
                {t('ichimokuTransition.drawer.invalidation')}:
              </Box>{' '}
              {getLocalizedText(current.signal?.invalidation_i18n, language, current.signal?.invalidation || '—')}
            </Typography>
            <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[700], lineHeight: 1.5 }}>
              <Box component="span" sx={{ fontWeight: 700, color: colors.gray[800] }}>
                {t('ichimokuTransition.drawer.stopReason')}:
              </Box>{' '}
              {getLocalizedText(current.signal?.stop_reason_i18n, language, current.signal?.stop_reason || '—')}
            </Typography>
          </Box>
        </Card>

        <Card>
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.gray[100]}` }}>
            <Typography sx={{ fontSize: fontSize.base, fontWeight: 800, color: colors.gray[800] }}>
              {t('ichimokuTransition.drawer.why')}
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack spacing={0.9}>
              {scoreBreakdown.length ? scoreBreakdown.map((item, index) => (
                <Box
                  key={`${item.component}-${item.feature}-${index}`}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: colors.gray[50],
                    border: `1px solid ${colors.gray[100]}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.35 }}>
                    <Typography sx={{ fontSize: fontSize.xs, fontWeight: 800, color: colors.gray[600], textTransform: 'uppercase' }}>
                      {getLocalizedText(item.component_label, language, item.component)}
                    </Typography>
                    <Chip label={`+${fmtNumber(item.points, 0)}`} size="small" sx={{ bgcolor: '#ecfeff', color: '#0891b2', fontWeight: 700, height: 22 }} />
                  </Box>
                  <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[800], fontWeight: 600, lineHeight: 1.45 }}>
                    {getLocalizedText(item.reason_i18n, language, item.reason)}
                  </Typography>
                </Box>
              )) : (
                <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[500] }}>
                  {t('ichimokuTransition.drawer.noBreakdown')}
                </Typography>
              )}
            </Stack>
          </Box>
        </Card>

        <Card>
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.gray[100]}` }}>
            <Typography sx={{ fontSize: fontSize.base, fontWeight: 800, color: colors.gray[800] }}>
              {t('ichimokuTransition.drawer.notes')}
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack spacing={0.75}>
              {reasons.map((item) => (
                <Typography key={item.en || item.vi} sx={{ fontSize: fontSize.sm, color: colors.gray[800], lineHeight: 1.45 }}>
                  • {getLocalizedText(item, language)}
                </Typography>
              ))}
              {warnings.map((item) => (
                <Typography key={item.en || item.vi} sx={{ fontSize: fontSize.sm, color: colors.warning.dark, lineHeight: 1.45 }}>
                  • {getLocalizedText(item, language)}
                </Typography>
              ))}
              {!reasons.length && !warnings.length ? (
                <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[500] }}>
                  {t('ichimokuTransition.drawer.noNotes')}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        </Card>

        <Card>
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.gray[100]}` }}>
            <Typography sx={{ fontSize: fontSize.base, fontWeight: 800, color: colors.gray[800] }}>
              {t('ichimokuTransition.drawer.history')}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>{t('ichimokuTransition.table.date')}</TableCell>
                  <TableCell sx={thSx}>{t('ichimokuTransition.table.state')}</TableCell>
                  <TableCell sx={thSx} align="center">{t('ichimokuTransition.table.score')}</TableCell>
                  <TableCell sx={thSx} align="right">{t('ichimokuTransition.table.close')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={`${row.score_date}-${row.state}`} hover>
                    <TableCell sx={tdSx}>{row.score_date || '—'}</TableCell>
                    <TableCell sx={{ ...tdSx, color: stateColor(row.state), fontWeight: 700 }}>
                      {getLocalizedText(row.state_label, language, getStateLabel(t, row.state))}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}>
                      {fmtNumber(row.score, 1)}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: 'right', fontFamily: 'monospace' }}>
                      {fmtPrice(row.close)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </>
    );
  } else if (!historyLoading) {
    drawerBody = <Alert severity="info">{t('ichimokuTransition.drawer.empty')}</Alert>;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 540 },
            bgcolor: colors.gray[50],
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: colors.background.paper,
          borderBottom: `1px solid ${colors.gray[100]}`,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: colors.gray[800], fontFamily: 'monospace' }}>
                {ticker || t('ichimokuTransition.drawer.title')}
              </Typography>
              {current?.state ? (
                <Chip
                  label={getLocalizedText(current.state_label, language, getStateLabel(t, current.state))}
                  size="small"
                  sx={{
                    bgcolor: `${stateColor(current.state)}16`,
                    color: stateColor(current.state),
                    fontWeight: 700,
                    height: 24,
                  }}
                />
              ) : null}
            </Box>
            <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[400], mt: 0.25 }}>
              {t('ichimokuTransition.drawer.subtitle')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: colors.gray[500] }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        {historyLoading ? <LinearProgress sx={{ mt: 1.25, borderRadius: 999 }} /> : null}
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {detail?.error ? <Alert severity="error">{detail.error}</Alert> : null}
        {drawerBody}
      </Box>
    </Drawer>
  );
}

function alphaBg(rr) {
  return (rr || 0) >= 1.5 ? `${colors.info.main}14` : colors.gray[100];
}

DetailDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  ticker: PropTypes.string,
  historyLoading: PropTypes.bool.isRequired,
  detail: PropTypes.shape({
    error: PropTypes.string,
    current: PropTypes.shape({
      score: PropTypes.number,
      setup_score: PropTypes.number,
      state: PropTypes.string,
      state_label: PropTypes.shape({
        vi: PropTypes.string,
        en: PropTypes.string,
      }),
      signal_type: PropTypes.string,
      signal_type_label: PropTypes.shape({
        vi: PropTypes.string,
        en: PropTypes.string,
      }),
      confidence: PropTypes.string,
      confidence_label: PropTypes.shape({
        vi: PropTypes.string,
        en: PropTypes.string,
      }),
      score_level: PropTypes.string,
      reasons: PropTypes.arrayOf(PropTypes.string),
      warnings: PropTypes.arrayOf(PropTypes.string),
      reasons_i18n: PropTypes.arrayOf(PropTypes.shape({
        vi: PropTypes.string,
        en: PropTypes.string,
      })),
      warnings_i18n: PropTypes.arrayOf(PropTypes.shape({
        vi: PropTypes.string,
        en: PropTypes.string,
      })),
      score_breakdown: PropTypes.arrayOf(PropTypes.shape({
        component: PropTypes.string,
        component_label: PropTypes.shape({
          vi: PropTypes.string,
          en: PropTypes.string,
        }),
        feature: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
        points: PropTypes.number,
        reason: PropTypes.string,
        reason_i18n: PropTypes.shape({
          vi: PropTypes.string,
          en: PropTypes.string,
        }),
      })),
      signal: PropTypes.shape({
        entry: PropTypes.number,
        stop: PropTypes.number,
        target_1: PropTypes.number,
        target_2: PropTypes.number,
        risk_pct: PropTypes.number,
        reward_pct: PropTypes.number,
        rr_ratio: PropTypes.number,
        trigger: PropTypes.string,
        invalidation: PropTypes.string,
        stop_reason: PropTypes.string,
        trigger_i18n: PropTypes.shape({
          vi: PropTypes.string,
          en: PropTypes.string,
        }),
        invalidation_i18n: PropTypes.shape({
          vi: PropTypes.string,
          en: PropTypes.string,
        }),
        stop_reason_i18n: PropTypes.shape({
          vi: PropTypes.string,
          en: PropTypes.string,
        }),
      }),
    }),
    history: PropTypes.arrayOf(PropTypes.shape({
      score_date: PropTypes.string,
      state: PropTypes.string,
      state_label: PropTypes.shape({
        vi: PropTypes.string,
        en: PropTypes.string,
      }),
      score: PropTypes.number,
      close: PropTypes.number,
    })),
    chart: PropTypes.shape({
      as_of_date: PropTypes.string,
      displacement: PropTypes.number,
      series: PropTypes.arrayOf(PropTypes.shape({
        date: PropTypes.string,
        open: PropTypes.number,
        high: PropTypes.number,
        low: PropTypes.number,
        close: PropTypes.number,
        tenkan: PropTypes.number,
        kijun: PropTypes.number,
        span_a: PropTypes.number,
        span_b: PropTypes.number,
        chikou: PropTypes.number,
        cloud_lower: PropTypes.number,
        bullish_cloud: PropTypes.number,
        bearish_cloud: PropTypes.number,
      })),
    }),
  }),
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
};

DetailDrawer.defaultProps = {
  ticker: null,
  detail: null,
};

export default DetailDrawer;
